import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { exactDecimalSchema } from "./faultContract";
import { formatApiError, requestSignal } from "./faultLink";
import { ApiError } from "./http";

// The load-run link of the power quality studies (EOS-11, Master Prompt A32): the selector's
// list of a revision's EOS-02 load runs, and the demand figures one chosen run holds. The
// backend mirror is app/services/load_link.py; the fault-run link (faultLink.ts) is the model.

const LOAD_RUNS_URL = "/api/v1/electrical/load-demand/runs";

/** The selector's own sentence for a study that names no load run of the revision. */
export const CHOOSE_A_LOAD_RUN = "Choose a load run of this project revision.";

// What the load-run selector shows from the runs list: summaries only, never a result field.
export const loadRunChoiceSchema = z
  .object({
    id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
    design_check_status: z.string(),
    jurisdiction_profile: z.string(),
  })
  .strict();

const loadRunListSchema = z
  .object({
    items: z.array(calculationRunSummarySchema),
  })
  .strict();

// The demand figures of one stored load run, read from its frozen result for display only:
// the backend resolves them again when the study is saved. Only these five fields are taken
// from the result snapshot; the rest is ignored.
const demandFiguresSnapshotSchema = z.object({
  group_code: z.string(),
  demand_power_kw: exactDecimalSchema,
  apparent_power_kva: exactDecimalSchema,
  reactive_power_kvar: exactDecimalSchema,
  status: z.string(),
});

const loadRunDetailSchema = calculationRunSummarySchema
  .extend({
    input_snapshot: z.record(z.string(), z.unknown()),
    result_snapshot: demandFiguresSnapshotSchema,
    warnings_snapshot: z.array(z.unknown()),
    references_snapshot: z.record(z.string(), z.unknown()),
  })
  .strict();

export const loadRunDemandFiguresSchema = demandFiguresSnapshotSchema
  .extend({
    run_id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
  })
  .strict();

export type LoadRunChoice = z.infer<typeof loadRunChoiceSchema>;
export type LoadRunDemandFigures = z.infer<typeof loadRunDemandFiguresSchema>;

/**
 * The load runs stored under one project revision, newest first, as the selector lists
 * them. The list carries run summaries only; the figures of the chosen run come from
 * getLoadRun.
 */
export async function listLoadRunsForRevision(
  projectRevisionId: string,
  signal?: AbortSignal,
): Promise<LoadRunChoice[]> {
  const query = new URLSearchParams({ project_revision_id: projectRevisionId });

  const response = await fetch(`${LOAD_RUNS_URL}?${query.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Load run list"), response.status);
  }

  const parsed = loadRunListSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS load runs API.");
  }

  return parsed.data.items.map((item) =>
    loadRunChoiceSchema.parse({
      id: item.id,
      calculation_key: item.calculation_key,
      revision_number: item.revision_number,
      design_check_status: item.design_check_status,
      jurisdiction_profile: item.jurisdiction_profile,
    }),
  );
}

/**
 * The demand figures one stored load run holds, for display beside the selector. Only the
 * five result fields the studies need are read; nothing shown here is authoritative.
 */
export async function getLoadRun(
  runId: string,
  signal?: AbortSignal,
): Promise<LoadRunDemandFigures> {
  const response = await fetch(`${LOAD_RUNS_URL}/${runId}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Load run"), response.status);
  }

  const parsed = loadRunDetailSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS load runs API.");
  }

  return loadRunDemandFiguresSchema.parse({
    run_id: parsed.data.id,
    calculation_key: parsed.data.calculation_key,
    revision_number: parsed.data.revision_number,
    ...parsed.data.result_snapshot,
  });
}
